import type { Signal } from '@preact/signals-react';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import type { Ref } from 'react';
import { useImperativeHandle, useRef, useState } from 'react';
import type { ContentEditableInputRef } from '../../content-editable-input/content-editable-input.component';
import ContentEditableInput from '../../content-editable-input/content-editable-input.component';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';

export interface DataFilterProps {
  ref?: Ref<DataFilterRef>;
  id: string | number;
  field: string;
  placeholder?: string;
  debounceTime?: number;
  filterSignal?: Signal<Array<ContinuousScrollerFilter>>;
  onChange?: (value: ContinuousScrollerFilter) => void;
  className?: string;
}

export interface DataFilterRef {
  focus: () => void;
}

const DataFilter = (props: DataFilterProps) => {
  const { ref, id, field, placeholder, debounceTime = 500, filterSignal, onChange, className } = props;

  const [showClear, setShowClear] = useState(false);
  const inputRef = useRef<ContentEditableInputRef>(null);

  const onClearClick = () => {
    inputRef.current?.clearText();
    setShowClear(false);
    inputRef.current?.focus();
  };

  const onTextChanged = (value: string) => {
    const filter: ContinuousScrollerFilter = {
      scrollerId: id,
      field,
      value,
      comparison: ContinuousScrollerComparisonType.Contains,
    };

    if (filterSignal) {
      const fieldIndex = filterSignal.value.findIndex((filter) => filter.field === field);
      const clonedFilters = cloneDeep(filterSignal.value);

      if (fieldIndex > -1) {
        clonedFilters.splice(fieldIndex, 1, filter);
        filterSignal.value = clonedFilters;
      } else {
        filterSignal.value = [...clonedFilters, filter];
      }
    }

    setShowClear(value.length > 0);
    onChange?.(filter);
  };

  const focus = () => {
    inputRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    focus,
  }));

  const wrapperStyle = clsx(
    'flex min-w-0 w-full items-center justify-center rounded-sm border border-input bg-background p-1 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50',
    className
  );

  return (
    <div className={wrapperStyle}>
      <ContentEditableInput
        ref={inputRef}
        className="relative block w-full grow py-1 leading-4 focus:outline-hidden"
        onTextChange={onTextChanged}
        placeholder={placeholder}
        debounceTime={debounceTime}
        isSingleLine={true}
      />
      {showClear && (
        <div className="mr-1" onClick={onClearClick}>
          <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} className="shrink cursor-pointer" />
        </div>
      )}
      <OdinIcon icon="Search" size={OdinIconSize.ExtraSmall} className="shrink" />
    </div>
  );
};

export default DataFilter;
